import React, { useState, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Heart, Loader2, Check } from "lucide-react";
import { toast, Toaster } from "sonner";
import axios from "axios";
import SectionReveal from "./SectionReveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Read `?g=N` from URL to cap max guests. Default = 5 (no restriction).
function readMaxGuestsFromURL() {
  try {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get("g");
    if (!raw) return 5;
    const n = parseInt(raw, 10);
    if (Number.isNaN(n) || n < 1) return 5;
    return Math.min(n, 10);
  } catch {
    return 5;
  }
}

export default function RSVPForm() {
  const maxGuests = useMemo(() => readMaxGuestsFromURL(), []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    attending: "yes",
    guests: 1,
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/rsvp`, {
        ...form,
        guests: Math.min(Number(form.guests) || 1, maxGuests),
      });
      setSubmitted(true);
      toast.success("RSVP received. JazakAllah khair!");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail) {
        toast.error(detail);
      } else {
        toast.error("Something went wrong. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="rsvp"
      data-testid="rsvp-section"
      className="relative py-20 px-6"
    >
      <Toaster
        position="top-center"
        toastOptions={{
          style: {
            fontFamily: "Outfit, sans-serif",
            background: "rgba(255,255,255,0.9)",
            color: "#0B1521",
            border: "1px solid rgba(197,168,128,0.4)",
            backdropFilter: "blur(12px)",
          },
        }}
      />

      <div className="max-w-md mx-auto text-center mb-10">
        <SectionReveal>
          <p className="uppercase text-[11px] tracking-[0.45em] text-ink/55 mb-3">
            Will you join us?
          </p>
          <h2 className="font-serif text-4xl sm:text-5xl italic text-ink">
            Kindly
            <br />
            <span className="shimmer-gold">respond</span>
          </h2>
          <p className="mt-4 text-sm text-ink/65 leading-relaxed">
            Your presence would mean the world to us. Please let us know by
            1st August, 2026.
          </p>
        </SectionReveal>
      </div>

      <SectionReveal delay={0.15}>
        <div className="max-w-md mx-auto">
          <AnimatePresence mode="wait">
            {submitted ? (
              <motion.div
                key="thanks"
                initial={{ opacity: 0, y: 20, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="glass-card rounded-[28px] p-10 text-center"
                data-testid="rsvp-success"
              >
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.3, type: "spring", stiffness: 180 }}
                  className="w-16 h-16 mx-auto rounded-full bg-champagne/20 flex items-center justify-center"
                >
                  <Check
                    size={26}
                    className="text-champagne"
                    strokeWidth={2}
                  />
                </motion.div>
                <h3 className="font-serif text-3xl italic text-ink mt-5">
                  JazakAllah khair
                </h3>
                <p className="font-arabic text-xl text-champagne mt-2">
                  جَزَاكَ ٱللَّٰهُ خَيْرًا
                </p>
                <p className="mt-4 text-sm text-ink/70">
                  Your RSVP has been received. We can&apos;t wait to celebrate
                  with you.
                </p>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={onSubmit}
                data-testid="rsvp-form"
                noValidate
                initial={{ opacity: 1 }}
                exit={{ opacity: 0, y: -20 }}
                className="glass-card rounded-[28px] p-6 sm:p-8 space-y-4"
              >
                <div>
                  <label className="block text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
                    Your Name *
                  </label>
                  <input
                    type="text"
                    data-testid="rsvp-input-name"
                    value={form.name}
                    onChange={(e) => update("name", e.target.value)}
                    placeholder="Full name"
                    className="wedding-input"
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
                    Phone (optional)
                  </label>
                  <input
                    type="tel"
                    data-testid="rsvp-input-phone"
                    value={form.phone}
                    onChange={(e) => update("phone", e.target.value)}
                    placeholder="+1 (555) 000-0000"
                    className="wedding-input"
                  />
                </div>

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
                    Will you attend?
                  </label>
                  <div
                    className="grid grid-cols-2 gap-3"
                    data-testid="rsvp-attending"
                  >
                    {[
                      { v: "yes", label: "Joyfully accept" },
                      { v: "no", label: "Regretfully decline" },
                    ].map((opt) => {
                      const active = form.attending === opt.v;
                      return (
                        <button
                          type="button"
                          key={opt.v}
                          data-testid={`rsvp-attending-${opt.v}`}
                          onClick={() => update("attending", opt.v)}
                          className={`relative rounded-xl px-4 py-3 text-xs uppercase tracking-[0.2em] transition-all border ${
                            active
                              ? "bg-ink text-cotton border-ink shadow-[0_6px_24px_-8px_rgba(11,21,33,0.4)]"
                              : "bg-white/40 text-ink/75 border-champagne/30 hover:border-champagne/60"
                          }`}
                        >
                          {opt.label}
                          {active && (
                            <motion.span
                              layoutId="attend-indicator"
                              className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-champagne"
                            />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {form.attending === "yes" && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.4 }}
                  >
                    <label className="block text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
                      Number of guests (including you)
                    </label>
                    <div
                      className="flex items-center gap-2"
                      data-testid="rsvp-guests"
                    >
                      {Array.from(
                        { length: Math.min(maxGuests, 10) },
                        (_, i) => i + 1
                      ).map((n) => {
                        const active = Number(form.guests) === n;
                        return (
                          <button
                            type="button"
                            key={n}
                            data-testid={`rsvp-guests-${n}`}
                            onClick={() => update("guests", n)}
                            className={`flex-1 h-11 rounded-xl font-serif italic text-lg transition-all border ${
                              active
                                ? "bg-champagne text-ink border-champagne"
                                : "bg-white/40 text-ink/70 border-champagne/30 hover:border-champagne/70"
                            }`}
                          >
                            {n}
                          </button>
                        );
                      })}
                    </div>
                  </motion.div>
                )}

                <div>
                  <label className="block text-[10px] uppercase tracking-[0.35em] text-ink/55 mb-2 pl-1">
                    A note for the couple
                  </label>
                  <textarea
                    data-testid="rsvp-input-message"
                    value={form.message}
                    onChange={(e) => update("message", e.target.value)}
                    placeholder="Du'a, wishes, dietary notes..."
                    rows={3}
                    className="wedding-input"
                  />
                </div>

                <motion.button
                  type="submit"
                  disabled={submitting}
                  data-testid="rsvp-submit"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                  className="w-full mt-2 relative overflow-hidden rounded-full bg-ink text-cotton py-4 font-sans text-sm uppercase tracking-[0.3em] disabled:opacity-60 transition-all shadow-[0_12px_28px_-12px_rgba(11,21,33,0.6)] hover:bg-ink/90"
                >
                  <span className="relative flex items-center justify-center gap-2.5">
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Heart size={14} strokeWidth={1.6} />
                        Send RSVP
                      </>
                    )}
                  </span>
                </motion.button>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </SectionReveal>
    </section>
  );
}
